import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';

export function useReturnToTabRefresh({ onReturn, minHiddenMs = 5 * 60 * 1000 } = {}) {
  const { instance, accounts } = useMsal();
  const hiddenAtRef = useRef(null);
  const onReturnRef = useRef(onReturn);
  onReturnRef.current = onReturn;

  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenFor = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
      hiddenAtRef.current = null;
      if (hiddenFor < minHiddenMs) return;

      // 1. Silent token refresh — keeps the ASD session alive after a long
      //    stint in the TIFO tab, with no visible interruption.
      const account = accounts?.[0];
      if (account) {
        try {
          await instance.acquireTokenSilent({
            account,
            scopes: ['openid', 'profile'], // swap in your API scopes if ASD calls a protected API
            forceRefresh: true
          });
        } catch {
          // Silent refresh not possible — let the next API call trigger
          // MSAL's normal interaction flow instead of forcing a login here.
        }
      }

      // 2. Refresh lightweight dashboard data so the view is current.
      if (typeof onReturnRef.current === 'function') {
        try {
          onReturnRef.current();
        } catch {
          // best-effort, never surface as an error
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [instance, accounts, minHiddenMs]);
}
